import { lazy, Suspense, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { ClinicalSourceReference } from './types';
const SourceViewer = lazy(() =>
  import('@/features/emr/components/UploadedDocumentInboxSheet').then((m) => ({
    default: m.UploadedDocumentInboxSheet,
  })),
);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function ClinicalEventEvidence({
  source,
  canReadDocument,
}: {
  source?: ClinicalSourceReference | null;
  canReadDocument: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!source) return null;
  if (source.kind !== 'reviewed_uploaded_ultrasound')
    return source.copied_from_event_id ? (
      <p className="text-sm">Reviewed copy from event {source.copied_from_event_id}</p>
    ) : null;
  const documentId =
    typeof source.document_id === 'string' && uuid.test(source.document_id) ? source.document_id : undefined;
  const revisionId =
    typeof source.revision_id === 'string' && uuid.test(source.revision_id) ? source.revision_id : undefined;
  return (
    <div className="mt-2 flex max-w-lg flex-col gap-2 rounded border p-3" aria-label="Ultrasound source evidence">
      <p className="font-semibold">Original uploaded ultrasound evidence</p>
      {typeof source.evidence_span === 'string' && (
        <blockquote className="whitespace-pre-wrap break-words">{source.evidence_span.slice(0, 12000)}</blockquote>
      )}
      <p className="break-all text-xs">
        Document: {documentId ?? 'Unavailable'} · Revision: {revisionId ?? 'Unavailable'} · Review version:{' '}
        {source.review_version ?? 'Unavailable'}
      </p>
      <p className="text-sm">
        Compare the original source revision, units, observation time and selected fetus before confirming clinical
        review. Source acceptance does not itself confirm this ledger observation.
      </p>
      <Button
        type="button"
        variant="outline"
        className="min-h-11 self-start"
        disabled={!canReadDocument || !documentId || !revisionId}
        onClick={() => setOpen(true)}>
        Open original source revision
      </Button>
      {open && documentId && revisionId && canReadDocument && (
        <Suspense fallback={<p>Loading source viewer…</p>}>
          <SourceViewer documentId={documentId} revisionId={revisionId} open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </div>
  );
}
